import React, {KeyboardEvent, useCallback, useEffect, useId, useLayoutEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";

export interface IMenuAction {
  id?: string;
  type?: "action" | "divider";
  label?: string;
  icon?: string;
  isDanger?: boolean;
  isDisabled?: boolean;
}

export interface IActionMenuProps {
  actions: IMenuAction[];
  record: Record<string, any>;
  onAction: (actionId: string, record: Record<string, any>) => void;
  triggerLabel?: string;
}

type MenuPosition = {top: number; left: number; placement: "up" | "down"; ready: boolean};

const VIEWPORT_GAP = 8;
const TRIGGER_GAP = 6;
const iconGlyphs: Record<string, string> = {
  pencil: "✎",
  eye: "◉",
  "eye-off": "◌",
  lock: "▣",
  "lock-open": "▢",
  trash: "⌫",
  refresh: "↻",
  plus: "+",
};

export default function ActionMenu({actions, record, onAction, triggerLabel = "Actions"}: IActionMenuProps) {
  const [open, setOpen] = useState(false);
  const [light, setLight] = useState(false);
  const [position, setPosition] = useState<MenuPosition>({top: 0, left: 0, placement: "down", ready: false});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = `action-menu-${useId().replace(/:/g, "")}`;

  const close = useCallback((restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) window.requestAnimationFrame(() => triggerRef.current?.focus());
  }, []);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !menuRef.current) return;
    const trigger = triggerRef.current.getBoundingClientRect();
    const menu = menuRef.current.getBoundingClientRect();
    const roomBelow = window.innerHeight - trigger.bottom - VIEWPORT_GAP;
    const roomAbove = trigger.top - VIEWPORT_GAP;
    const openUp = menu.height > roomBelow && roomAbove > roomBelow;
    const desiredTop = openUp ? trigger.top - menu.height - TRIGGER_GAP : trigger.bottom + TRIGGER_GAP;
    const desiredLeft = trigger.right + TRIGGER_GAP;
    setPosition({
      top: Math.max(VIEWPORT_GAP, Math.min(desiredTop, window.innerHeight - menu.height - VIEWPORT_GAP)),
      left: Math.max(VIEWPORT_GAP, Math.min(desiredLeft, window.innerWidth - menu.width - VIEWPORT_GAP)),
      placement: openUp ? "up" : "down",
      ready: true,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    const firstItem = menuRef.current?.querySelector<HTMLButtonElement>('button[role="menuitem"]:not(:disabled)');
    firstItem?.focus();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!menuRef.current?.contains(target) && !triggerRef.current?.contains(target)) close();
    };
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); close(true); }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [close, open, updatePosition]);

  const toggle = () => {
    if (!open) {
      setLight(Boolean(triggerRef.current?.closest(".light")));
      setPosition({top: 0, left: 0, placement: "down", ready: false});
    }
    setOpen(value => !value);
  };

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (["Enter", " ", "ArrowDown"].includes(event.key)) { event.preventDefault(); if (!open) toggle(); }
  };

  const handleMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('button[role="menuitem"]:not(:disabled)') || []);
    if (!items.length) return;
    const current = items.indexOf(document.activeElement as HTMLButtonElement);
    let next = current;
    if (event.key === "ArrowDown") next = (current + 1) % items.length;
    else if (event.key === "ArrowUp") next = (current - 1 + items.length) % items.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = items.length - 1;
    else if (event.key === "Tab") {
      event.preventDefault();
      const focusable = Array.from(document.querySelectorAll<HTMLElement>(
        'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
      )).filter(element => !menuRef.current?.contains(element) && element.getAttribute("aria-hidden") !== "true");
      const triggerIndex = focusable.indexOf(triggerRef.current as HTMLButtonElement);
      const targetIndex = event.shiftKey ? triggerIndex - 1 : triggerIndex + 1;
      (focusable[targetIndex] || triggerRef.current)?.focus();
      close();
      return;
    }
    else return;
    event.preventDefault();
    items[next]?.focus();
  };

  const selectAction = (action: IMenuAction) => {
    if (!action.id || action.isDisabled) return;
    close();
    onAction(action.id, record);
  };

  if (!actions.some(action => action.type !== "divider")) return null;

  return <>
    <button
      ref={triggerRef}
      type="button"
      className="action-menu-trigger"
      title={triggerLabel}
      aria-label={triggerLabel}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-controls={open ? menuId : undefined}
      onClick={toggle}
      onKeyDown={handleTriggerKeyDown}
    >⋮</button>
    {open && createPortal(
      <div
        ref={menuRef}
        id={menuId}
        role="menu"
        aria-label={triggerLabel}
        className={`action-menu-popover${light ? " light" : ""}`}
        data-placement={position.placement}
        onKeyDown={handleMenuKeyDown}
        style={{top: position.top, left: position.left, visibility: position.ready ? "visible" : "hidden"}}
      >
        {actions.map((action, index) => action.type === "divider" ?
          <div key={`divider-${index}`} className="action-menu-divider" role="separator"/> :
          <button
            key={action.id || `action-${index}`}
            type="button"
            role="menuitem"
            title={action.label}
            className={action.isDanger ? "danger" : undefined}
            disabled={action.isDisabled || !action.id}
            onClick={() => selectAction(action)}
          >
            <span className="action-menu-icon" aria-hidden="true">{action.icon ? iconGlyphs[action.icon] || action.icon : ""}</span>
            <b>{action.label}</b>
          </button>)}
      </div>,
      document.body,
    )}
  </>;
}
